import { For, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";

import clsx from "~/util/html/style";

import type { ArtKey } from "../types";
import { ArchToothShape } from "./arch_art";
import styles from "./art.module.css";
import { useArtRef } from "./art_defs";
import {
  ARCH_TEETH,
  GUM_BANDS,
  GUM_INNER_WIDTH,
  GUM_OUTER_WIDTH,
  UPPER_PALATE,
} from "./arch_geometry";
import { CrownCap, Tissue, ToothBody } from "./tooth_art";
import {
  CROWN,
  CROWN_INLAY,
  INCISOR,
  INCISOR_CROWN,
  INCISOR_FACE,
  INCISOR_PULP,
  INLAY,
  INLAY_FIT,
  PULP,
  SOCKET_OUTLINE,
  TOOTH_INLAY,
  VENEER_SHELL,
} from "./tooth_paths";

type Drawing = {
  viewBox: string;
  body: () => JSX.Element;
  /** Seen from above: the arch's tooth styles apply. */
  arch?: boolean;
};

/** One picture per treatment family for its explanation page. Drawings of the treatment, not icons. */
export function ProcedureArt(props: { art: ArtKey; title: string }): JSX.Element {
  const drawing = () => DRAWINGS[props.art];
  return (
    <svg
      viewBox={drawing().viewBox}
      class={clsx(styles["tooth-art"], drawing().arch && styles["arch"])}
      role="img"
      aria-label={props.title}
    >
      <title>{props.title}</title>
      <Dynamic component={drawing().body} />
    </svg>
  );
}

// The cap's outline without its base, dashed where a crown will sit.
const CAP_FIT =
  "M18,80 C12,52 24,18 44,16 C52,15 56,25 60,25 C64,25 68,15 76,16 C96,18 108,52 102,80";

function BrandArrow(props: { d: string }): JSX.Element {
  return (
    <path
      d={props.d}
      fill="none"
      class={styles["brand-stroke"]}
      stroke-width="2.6"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  );
}

/** Dashed in the brand color: where a new piece goes. */
function FitLine(props: { d: string }): JSX.Element {
  return (
    <path
      d={props.d}
      fill="none"
      class={styles["brand-stroke"]}
      stroke-width="1.4"
      stroke-dasharray="3 4"
      opacity=".7"
    />
  );
}

function Shadow(props: { rx: number; ry: number }): JSX.Element {
  return (
    <ellipse
      cx="60"
      cy="174"
      rx={props.rx}
      ry={props.ry}
      fill="#0D2430"
      opacity=".16"
      filter={useArtRef("f-blur3")}
    />
  );
}

/** A four-point star. */
function Sparkle(props: { x: number; y: number; r: number }): JSX.Element {
  const d = () => {
    const [x, y, r] = [props.x, props.y, props.r];
    const k = r * 0.22;
    const n = (v: number) => Number(v.toFixed(2));
    return (
      `M${n(x)},${n(y - r)} C${n(x + k)},${n(y - k)} ${n(x + k)},${n(y - k)} ${n(x + r)},${n(y)} ` +
      `C${n(x + k)},${n(y + k)} ${n(x + k)},${n(y + k)} ${n(x)},${n(y + r)} ` +
      `C${n(x - k)},${n(y + k)} ${n(x - k)},${n(y + k)} ${n(x - r)},${n(y)} ` +
      `C${n(x - k)},${n(y - k)} ${n(x - k)},${n(y - k)} ${n(x)},${n(y - r)} Z`
    );
  };
  return <path d={d()} class={styles["brand-fill"]} />;
}

/** The upper teeth from above, as the arch drawing places them. */
function UpperTeeth(): JSX.Element {
  return <For each={ARCH_TEETH.filter((t) => t.upper)}>{(t) => <ArchToothShape tooth={t} />}</For>;
}

function UpperBand(props: { class: string | undefined; width: number }): JSX.Element {
  return (
    <path
      d={GUM_BANDS[0]}
      class={props.class}
      fill="none"
      stroke-width={props.width}
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  );
}

function Crown(): JSX.Element {
  return (
    <>
      <ToothBody spec={{ stump: true, pulp: "ok", shadow: true }} />
      <FitLine d={CAP_FIT} />
      <g transform="translate(0,-84)">
        <CrownCap />
      </g>
      <BrandArrow d="M60,9 L60,26 M53,20 L60,27 L67,20" />
    </>
  );
}

// The implant's threads, one ridge per turn down the screw's tapering sides.
const THREADS = [112, 121, 130, 139, 148].map((y, k) => {
  const left = (45.4 + k * 0.38).toFixed(1);
  const right = (74.6 - k * 0.38).toFixed(1);
  return {
    line: `M${left},${y} L${right},${y - 5}`,
    ridges: `M${left},${y - 3} l-4,2.5 l4,2.5 M${right},${y - 8} l4,2.5 l-4,2.5`,
  };
});

function Implant(): JSX.Element {
  return (
    <>
      <Tissue gum={86} bone={102} />
      <path
        d="M45,100 L75,100 L73,150 C72,158 67,162 60,162 C53,162 48,158 47,150 Z"
        fill={useArtRef("g-steel")}
        stroke="#5E6A72"
        stroke-width="1.4"
        stroke-linejoin="round"
      />
      <For each={THREADS}>
        {(thread) => (
          <>
            <path
              d={thread.line}
              stroke="#5E6A72"
              stroke-width="1.6"
              stroke-linecap="round"
              opacity=".55"
            />
            <path
              d={thread.ridges}
              fill="#B7C0C6"
              stroke="#5E6A72"
              stroke-width="1.1"
              stroke-linejoin="round"
            />
          </>
        )}
      </For>
      <path
        d="M49,70 L71,70 L74,100 L46,100 Z"
        fill={useArtRef("g-steel")}
        stroke="#5E6A72"
        stroke-width="1.4"
        stroke-linejoin="round"
      />
      <CrownCap />
    </>
  );
}

function Filling(): JSX.Element {
  return <ToothBody spec={{ overlays: ["small_filling"], pulp: "ok", shadow: true }} />;
}

function Bridge(): JSX.Element {
  return (
    <>
      <Tissue gum={86} bone={104} centers={[60, 180, 300]} x1={374} />
      <rect x="58" y="44" width="244" height="12" rx="6" class={styles["brand-fill"]} />
      <For each={[0, 240]}>
        {(dx) => (
          <g transform={`translate(${dx},0)`}>
            <ToothBody spec={{ stump: true, pulp: "ok" }} />
            <CrownCap />
          </g>
        )}
      </For>
      <g transform="translate(120,0)">
        <CrownCap />
      </g>
    </>
  );
}

function Orthodontics(): JSX.Element {
  return (
    <>
      <Tissue gum={84} bone={104} centers={[60, 180, 300]} x1={374} />
      <For each={[0, 120, 240]}>
        {(dx) => (
          <g transform={`translate(${dx},0)`}>
            <ToothBody spec={{ pulp: "ok" }} />
          </g>
        )}
      </For>
      <For each={[60, 180, 300]}>
        {(cx) => (
          <>
            <rect
              x={cx - 15}
              y="36"
              width="30"
              height="28"
              rx="5"
              fill={useArtRef("g-steel")}
              stroke="#5E6A72"
              stroke-width="1.4"
            />
            <path
              d={`M${cx - 15},44 h30 M${cx - 15},56 h30`}
              stroke="#5E6A72"
              stroke-width="1.2"
              opacity=".55"
            />
            <path
              d={`M${cx - 9},40 C${cx - 4},38.6 ${cx + 4},38.6 ${cx + 9},40`}
              fill="none"
              stroke="#FFFFFF"
              stroke-width="2"
              stroke-linecap="round"
              opacity=".7"
            />
          </>
        )}
      </For>
      <path
        d="M10,50 L350,50"
        fill="none"
        class={styles["brand-stroke"]}
        stroke-width="4.4"
        stroke-linecap="round"
      />
    </>
  );
}

function Dentures(): JSX.Element {
  return (
    <>
      <path
        d={GUM_BANDS[0]}
        fill="none"
        stroke="#0F232B"
        stroke-opacity=".12"
        stroke-width={GUM_OUTER_WIDTH}
        stroke-linecap="round"
        stroke-linejoin="round"
        transform="translate(0 7)"
        filter={useArtRef("f-blur6")}
      />
      <UpperBand class={styles["denture-base-o"]} width={GUM_OUTER_WIDTH} />
      <UpperBand class={styles["denture-base-i"]} width={GUM_INNER_WIDTH} />
      <path d={UPPER_PALATE} class={styles["palate"]} />
      <UpperTeeth />
    </>
  );
}

function Extraction(): JSX.Element {
  return (
    <>
      <Tissue gum={86} bone={104} />
      <path d={`${SOCKET_OUTLINE} Z`} fill="#7E2F35" fill-opacity=".16" />
      <path
        d={SOCKET_OUTLINE}
        fill="none"
        stroke="#9AA7AF"
        stroke-width="1.8"
        stroke-dasharray="5 5"
      />
      <g transform="translate(0,-100) rotate(-5 60 90)">
        <ToothBody spec={{ pulp: "ok" }} />
      </g>
      <BrandArrow d="M60,86 L60,66 M53,73 L60,66 L67,73" />
    </>
  );
}

function RootCanal(): JSX.Element {
  return <ToothBody spec={{ overlays: ["canals_filled"], shadow: true }} />;
}

function DeepCleaning(): JSX.Element {
  return (
    <ToothBody
      spec={{
        overlays: ["tartar", "scaler"],
        gum: 88,
        bone: 112,
        pulp: "ok",
        pocket: true,
        hotGums: true,
      }}
    />
  );
}

const GRAFT = "M38,98 C37,122 45,146 60,149 C75,146 83,122 82,98 C70,94 50,94 38,98 Z";
// Graft granules, denser than the bone around them.
const GRANULES: readonly (readonly [number, number, number])[] = [
  [46, 106, 2.4],
  [55, 103, 1.8],
  [66, 104, 2.2],
  [75, 107, 1.9],
  [50, 116, 2.0],
  [61, 113, 2.6],
  [71, 118, 2.1],
  [45, 125, 1.7],
  [55, 125, 2.2],
  [66, 127, 1.8],
  [76, 126, 1.6],
  [51, 136, 2.3],
  [61, 138, 1.9],
  [70, 135, 2.0],
  [57, 145, 1.6],
  [64, 146, 1.5],
];

function BoneGraft(): JSX.Element {
  return (
    <>
      <Tissue gum={84} bone={100} centers={[-32, 60, 152]} x0={-106} x1={226} />
      <For each={[-92, 92]}>
        {(dx) => (
          <g transform={`translate(${dx},0)`}>
            <ToothBody spec={{ pulp: "ok" }} />
          </g>
        )}
      </For>
      <path d={GRAFT} fill="#EADCC0" />
      <For each={GRANULES}>{([x, y, r]) => <circle cx={x} cy={y} r={r} fill="#D6BF93" />}</For>
      <path
        d={GRAFT}
        fill="none"
        class={styles["brand-stroke"]}
        stroke-width="1.8"
        stroke-dasharray="4 3.2"
        stroke-linejoin="round"
      />
    </>
  );
}

function Sedation(): JSX.Element {
  return (
    <>
      <circle cx="60" cy="86" r="54" class={styles["brand-soft-fill"]} />
      <circle
        cx="60"
        cy="86"
        r="54"
        fill="none"
        class={styles["brand-soft-stroke"]}
        stroke-width="3"
      />
      <path d="M66,46 A28,28 0 1 0 84,94 A23,23 0 1 1 66,46 Z" class={styles["brand-fill"]} />
      <Sparkle x={84} y={58} r={7} />
      <Sparkle x={94} y={80} r={4.6} />
      <Sparkle x={78} y={112} r={5.4} />
    </>
  );
}

function NightGuard(): JSX.Element {
  return (
    <>
      <UpperBand class={styles["gum-o"]} width={GUM_OUTER_WIDTH} />
      <UpperBand class={styles["gum-i"]} width={GUM_INNER_WIDTH} />
      <UpperTeeth />
      {/* The guard: a clear tray over the biting edges, its rim in the brand color. */}
      <g opacity=".55">
        <UpperBand class={styles["brand-stroke"]} width={78} />
        <UpperBand class={styles["brand-soft-band"]} width={70} />
      </g>
    </>
  );
}

// New gum over the exposed root, faded into the gum below; its dashed edge is the new gum line.
const GUM_GRAFT =
  "M-6,92 C10,86 20,85 30,89 C42,93.6 52,93 60,92.4 C68,93 78,93.6 90,89 C100,85 110,86 126,92 " +
  "L126,128 L-6,128 Z";
const GRAFT_MARGIN =
  "M18,86.6 C24,86.2 27,87.6 30,89 C42,93.6 52,93 60,92.4 C68,93 78,93.6 90,89 C93,87.6 96,86.2 102,86.6";

function GumSurgery(): JSX.Element {
  return (
    <>
      <ToothBody spec={{ gum: 100, bone: 116, pulp: "ok" }} />
      <g mask={useArtRef("m-vignette")}>
        <path d={GUM_GRAFT} fill="#F9D3CE" fill-opacity=".94" />
      </g>
      <path
        d={GRAFT_MARGIN}
        fill="none"
        stroke="#FFFFFF"
        stroke-opacity=".8"
        stroke-width="1.8"
        stroke-linecap="round"
      />
      <path
        d={GRAFT_MARGIN}
        fill="none"
        class={styles["brand-stroke"]}
        stroke-width="1.8"
        stroke-dasharray="4 3.2"
        stroke-linecap="round"
      />
    </>
  );
}

function Veneer(): JSX.Element {
  return (
    <>
      <Shadow rx={26} ry={5} />
      <path d={INCISOR} fill={useArtRef("g-root")} />
      <path d={INCISOR_CROWN} fill={useArtRef("g-enamel")} />
      <path d={INCISOR_PULP} fill={useArtRef("g-pulp")} />
      <path d={INCISOR} fill="none" stroke="#8E9CA5" stroke-width="1.7" />
      <path d="M41,84 C53,88.4 67,88.4 79,84" fill="none" stroke="#D6C9B3" stroke-width="1.2" />
      <path
        d="M46.4,62 C49.6,52 52.4,36 55.4,24"
        fill="none"
        stroke="#FFFFFF"
        stroke-width="3.4"
        stroke-linecap="round"
        opacity=".9"
      />
      <FitLine d={INCISOR_FACE} />
      <g transform="translate(10,-3)">
        <path
          d={VENEER_SHELL}
          fill={useArtRef("g-porcelain")}
          class={styles["brand-stroke"]}
          stroke-width="2.2"
          stroke-linejoin="round"
        />
      </g>
    </>
  );
}

function InlayOnlay(): JSX.Element {
  return (
    <>
      <Shadow rx={38} ry={6} />
      <path d={TOOTH_INLAY} fill={useArtRef("g-root")} />
      <path d={CROWN_INLAY} fill={useArtRef("g-enamel")} />
      <path d={PULP} fill={useArtRef("g-pulp")} />
      <path d={TOOTH_INLAY} fill="none" stroke="#8E9CA5" stroke-width="1.7" />
      <path d="M23,78 C40,84 80,84 97,78" fill="none" stroke="#D6C9B3" stroke-width="1.2" />
      <path
        d="M31,52 C32,39 38,29 47,26"
        fill="none"
        stroke="#FFFFFF"
        stroke-width="4"
        stroke-linecap="round"
        opacity=".95"
      />
      <FitLine d={INLAY_FIT} />
      <g transform="translate(0,-46)">
        <path
          d={INLAY}
          fill={useArtRef("g-porcelain")}
          class={styles["brand-stroke"]}
          stroke-width="2.4"
          stroke-linejoin="round"
        />
      </g>
    </>
  );
}

function Whitening(): JSX.Element {
  return (
    <>
      <ToothBody spec={{ pulp: "ok", shadow: true }} />
      <path d={CROWN} fill={useArtRef("g-whiten")} />
      <Sparkle x={92} y={20} r={9} />
      <Sparkle x={106} y={44} r={5.5} />
      <Sparkle x={80} y={2} r={4.5} />
    </>
  );
}

const DRAWINGS: Record<ArtKey, Drawing> = {
  crown: { viewBox: "0 -76 120 262", body: Crown },
  implant: { viewBox: "0 0 120 176", body: Implant },
  filling: { viewBox: "0 0 120 184", body: Filling },
  bridge: { viewBox: "0 -4 360 184", body: Bridge },
  orthodontics: { viewBox: "0 0 360 176", body: Orthodontics },
  dentures: { viewBox: "4 10 580 272", body: Dentures, arch: true },
  extraction: { viewBox: "0 -96 120 280", body: Extraction },
  root_canal: { viewBox: "0 0 120 184", body: RootCanal },
  deep_cleaning: { viewBox: "0 0 120 176", body: DeepCleaning },
  // Both neighbors whole: the art panel does not clip, so anything past the viewBox lands on the slide's text.
  bone_graft: { viewBox: "-92 0 304 176", body: BoneGraft },
  sedation: { viewBox: "0 0 120 176", body: Sedation },
  night_guard: { viewBox: "4 10 580 272", body: NightGuard, arch: true },
  gum_surgery: { viewBox: "0 0 120 176", body: GumSurgery },
  veneer: { viewBox: "0 0 120 184", body: Veneer },
  inlay_onlay: { viewBox: "0 -40 120 224", body: InlayOnlay },
  whitening: { viewBox: "0 0 120 184", body: Whitening },
};
